import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { Page, PageHeader, StatusBadge } from "@/components/Bits";
import ReleaseScheduleModal from "@/components/ReleaseScheduleModal";
import CreateAdjustmentModal from "@/components/CreateAdjustmentModal";
import BalanceCheckModal from "@/components/BalanceCheckModal";
import TreatmentRules from "@/components/TreatmentRules";
import SyncStatus from "@/components/SyncStatus";
import ImportFromXeroModal from "@/components/ImportFromXeroModal";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DownloadSimple, CaretRight, Tray, FileArrowUp, FileCsv, Sparkle, Warning, X, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useReturnToClose } from "@/hooks/useReturnToClose";

function parseCsv(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim().length);
  if (!lines.length) return [];
  const splitLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (c === "," && !q) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur); return out;
  };
  const header = splitLine(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((l) => {
    const cells = splitLine(l);
    const row = {};
    header.forEach((h, idx) => { row[h] = (cells[idx] || "").trim(); });
    return row;
  });
}

export default function Unscheduled() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [check, setCheck] = useState(null); // {data, rows}
  const [showBulk, setShowBulk] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAddOpening, setShowAddOpening] = useState(false);
  const [bulking, setBulking] = useState(false);
  const [skippedSources, setSkippedSources] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [removed, setRemoved] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const loadRemoved = useCallback(() => {
    api.get("/unscheduled/dismissed").then(({ data }) => setRemoved(data.removed || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/unscheduled");
      setItems(data.items);
      setSelected(new Set());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); loadRemoved(); }, [load, loadRemoved]);
  useEffect(() => {
    const onDone = () => load();
    window.addEventListener("import:done", onDone);
    return () => window.removeEventListener("import:done", onDone);
  }, [load]);
  useReturnToClose(items.length === 0, !loading);

  const handleImported = (result) => {
    toast.success(`Imported ${result.imported} new item(s)`);
    const skippedSrc = result.skipped_sources || [];
    setSkippedSources(skippedSrc);
    if (skippedSrc.length) {
      const names = [...new Set(skippedSrc.map((s) => s.source))].join(", ");
      toast.warning(`Some Xero data was skipped (${names}). Reconnect Xero to grant full access.`, { duration: 8000 });
    }
    load();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import-sheet/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "accrued-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const runBulkAI = async () => {
    setBulking(true);
    try {
      const { data } = await api.post("/schedules/ai-suggest-bulk");
      const skipped = data.skipped || [];
      if (data.created > 0)
        toast.success(`AI scheduled ${data.created} item(s)${skipped.length ? `; ${skipped.length} left for review` : ""}`);
      else
        toast.warning(skipped.length ? `Nothing auto-scheduled; ${skipped.length} item(s) need review` : (data.message || "Nothing to schedule"));
      setShowBulk(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk AI-schedule failed");
    } finally { setBulking(false); }
  };

  const restore = async (itemId) => {
    try {
      await api.post(`/unscheduled/${itemId}/restore`);
      toast.success("Item restored to the inbox");
      load(); loadRemoved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore this item"); }
  };

  const deleteOne = async (it) => {
    try {
      await api.delete(`/unscheduled/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(it.id); return n; });
      loadRemoved();
      toast.success(`Removed ${it.doc_number}`, { action: { label: "Undo", onClick: () => restore(it.id) } });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove this item"); }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/unscheduled/bulk-delete", { ids });
      toast.success(`Removed ${data.removed} item(s) — they won't re-import until restored`);
      load(); loadRemoved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove items"); }
    finally { setDeleting(false); }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text)
        .filter((r) => (r.category || "").length && (r.source_account_code || "").length)
        .map((r) => ({
          category: r.category, source_account_code: r.source_account_code,
          description: r.description || "", reference: r.reference || "",
          original_amount: parseFloat(r.original_amount || "0") || 0,
          remaining_amount: (r.remaining_amount ?? "").toString().trim() !== "" ? parseFloat(r.remaining_amount) : null,
          start_date: r.start_date, end_date: r.end_date, frequency: r.frequency,
          num_periods: r.num_periods ? parseInt(r.num_periods, 10) : null,
          pl_account_code: r.pl_account_code,
          pl_split: r.pl_split || "",
          tracking1_name: r.tracking1_name || "", tracking1_option: r.tracking1_option || "",
          tracking2_name: r.tracking2_name || "", tracking2_option: r.tracking2_option || "",
        }));
      if (!rows.length) { toast.error("No valid rows found in the file"); return; }
      const { data } = await api.post("/import-sheet/check", { rows });
      setCheck({ data, rows });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not read the file");
    } finally { setUploading(false); }
  };

  return (
    <Page>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="sheet-file-input" />
      <PageHeader overline="Month-end" title="To Review"
        subtitle="Imported balances awaiting a release schedule — or bulk-import opening balances & release-only schedules from a sheet.">
        <button onClick={downloadTemplate} data-testid="download-template-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all">
          <FileCsv size={18} /> Template
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-sheet-btn"
          title="Import many opening balances at once — no set-up journals are posted (release-only)."
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all disabled:opacity-60">
          <FileArrowUp size={18} /> {uploading ? "UPLOADING…" : "Bulk release-only import"}
        </button>
        <button onClick={() => setShowAddOpening(true)} data-testid="add-opening-balance-btn"
          title="Capture a balance that's already in Xero but the sync missed — no set-up journal is posted."
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-orange/40 text-orangeink hover:bg-orange hover:text-white transition-all">
          <FileArrowUp size={18} /> Add opening balance
        </button>
        {items.length > 0 && (
          <button onClick={() => setShowBulk(true)} data-testid="bulk-ai-schedule-btn"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors duration-200">
            <Sparkle size={18} weight="fill" /> AI-schedule all
          </button>
        )}
        <button onClick={() => setShowImport(true)} data-testid="run-import-btn"
          className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
          <DownloadSimple size={18} /> Import from Xero
        </button>
      </PageHeader>

      {skippedSources.length > 0 && (
        <div data-testid="reconnect-xero-banner"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4">
          <Warning size={22} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold">Some Xero data couldn't be imported</p>
            <p className="text-amber-800 mt-0.5">
              {[...new Set(skippedSources.map((s) => s.source))].join(", ")} {skippedSources.length === 1 ? "was" : "were"} skipped because your Xero connection is missing the required permission. Reconnect Xero to grant full access and import everything.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => navigate("/settings?tab=connect")} data-testid="reconnect-xero-btn"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors duration-200">
              Reconnect Xero
            </button>
            <button onClick={() => setSkippedSources([])} data-testid="dismiss-reconnect-banner-btn"
              className="p-2 text-amber-500 hover:text-amber-700 transition-colors" aria-label="Dismiss">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <SyncStatus onSynced={load} />
      <TreatmentRules onApplied={load} />

      {removed.length > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-navy/[0.02] px-4 py-3" data-testid="unscheduled-removed-strip">
          <div className="flex items-center gap-2 mb-2">
            <ArrowCounterClockwise size={15} className="text-navy/70" />
            <span className="overline text-navy/70">Recently removed · last 24h — won't re-import until restored</span>
          </div>
          <div className="flex flex-col gap-2">
            {removed.map((r) => (
              <div key={r.item_id} data-testid={`removed-${r.item_id}`}
                className="flex items-center justify-between gap-3 text-sm bg-white border border-slate-100 rounded-lg px-3 py-2">
                <div className="min-w-0 truncate">
                  <span className="font-medium text-navy">{r.doc_number}</span>
                  <span className="text-navy/65"> · {r.contact}</span>
                  <span className="ml-2 font-mono text-navy/70">{money(r.amount, r.currency)}</span>
                </div>
                <button onClick={() => restore(r.item_id)} data-testid={`restore-${r.item_id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange/40 text-orangeink hover:bg-orange hover:text-white transition-colors">
                  <ArrowCounterClockwise size={13} /> Undo
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-orange/30 bg-orange/[0.06] px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-navy">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn"
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Clear</button>
            <button onClick={deleteSelected} disabled={deleting} data-testid="bulk-delete-btn"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60">
              <Trash size={15} /> {deleting ? "Removing…" : `Remove ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 w-10 bg-slate-50">
                {items.length > 0 && (
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="select-all-checkbox"
                    className="cursor-pointer accent-orange" aria-label="Select all" />
                )}
              </th>
              <Th>Date</Th><Th>Organisation</Th><Th>Document</Th><Th>Contact</Th><Th>Code</Th><Th>Type</Th>
              <Th right>Amount</Th><Th /><Th />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`unscheduled-row-${it.id}`}
                onClick={() => setActive(it)}
                className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors group ${selected.has(it.id) ? "bg-orange/[0.04]" : ""}`}>
                <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)}
                    data-testid={`select-${it.id}`} className="cursor-pointer accent-orange" aria-label={`Select ${it.doc_number}`} />
                </td>
                <Td mono>{fmtDate(it.date)}</Td>
                <Td>{it.tenant_name}</Td>
                <Td mono>{it.doc_number}</Td>
                <Td>{it.contact}<div className="text-xs text-slate-600">{it.description}</div></Td>
                <Td mono>{it.account_code}</Td>
                <Td><StatusBadge value={it.category} /></Td>
                <Td right mono className="font-semibold text-navy">{money(it.amount, it.currency)}</Td>
                <td className="px-2 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => deleteOne(it)} data-testid={`delete-${it.id}`} title="Remove — won't re-import until restored"
                    className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/65 hover:text-red-600 hover:border-red-300 transition-colors">
                    <Trash size={15} />
                  </button>
                </td>
                <Td right><CaretRight size={16} className="text-slate-300 group-hover:text-orangeink transition-colors" /></Td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && items.length === 0 && (
          <div className="py-16 text-center" data-testid="unscheduled-empty">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto"><Tray size={28} className="text-slate-600" /></div>
            <div className="font-heading font-bold text-lg text-navy mt-4">No unscheduled items</div>
            <p className="text-sm text-slate-600 mt-1">Select organisations, then run an import.</p>
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      <ReleaseScheduleModal item={active} open={!!active} onClose={() => setActive(null)}
        onCreated={load} />

      <ImportFromXeroModal open={showImport} onClose={() => setShowImport(false)} onImported={handleImported} />

      <CreateAdjustmentModal open={showAddOpening} onClose={() => setShowAddOpening(false)}
        onCreated={() => { setShowAddOpening(false); load(); navigate("/register"); }} />

      <BalanceCheckModal open={!!check} data={check?.data} rows={check?.rows}
        onClose={() => setCheck(null)}
        onCommitted={(info) => {
          setCheck(null); load();
          if ((info?.created || 0) > 0) navigate("/register");
        }} />

      <Dialog open={showBulk} onOpenChange={(o) => !o && setShowBulk(false)}>
        <DialogContent className="max-w-md rounded-2xl border-slate-200" data-testid="bulk-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl text-navy flex items-center gap-2">
              <Sparkle size={20} weight="fill" className="text-[#C2410C]" /> AI-schedule all items
            </DialogTitle>
            <DialogDescription className="text-navy/70">
              The AI reviews each of the {items.length} unscheduled item(s) and creates a release schedule where it can
              confidently match a period and P&amp;L code. Anything uncertain is left here for you to schedule manually.
              No journals are posted to Xero by this step.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
            <button onClick={runBulkAI} disabled={bulking} data-testid="bulk-ai-confirm-btn"
              className="px-5 py-2 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors disabled:opacity-60">
              {bulking ? "SCHEDULING…" : "Schedule with AI"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

const Th = ({ children, right }) => <th className={`px-5 py-3 ${right ? "text-right" : "text-left"} font-semibold text-[0.66rem] uppercase tracking-wider text-slate-600 bg-slate-50 ${right ? "" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) =>
  <td className={`px-5 py-3.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;
